import type { GameOptions, Move, PlayerView, GameSummary } from './game.js';

export type ReactionKind = 'hi' | 'bye' | 'cry' | 'laugh' | 'awesome' | 'gg';
export const REACTION_KINDS: ReactionKind[] = ['hi', 'bye', 'laugh', 'awesome', 'cry', 'gg'];

/** Events client -> server */
export interface ClientToServerEvents {
  'game:create': (opts: Partial<GameOptions>, ack: (res: AckResult<{ code: string; gameId: string }>) => void) => void;
  'game:join': (payload: { code: string }, ack: (res: AckResult<{ gameId: string }>) => void) => void;
  'game:addBot': (payload: { gameId: string }, ack?: (res: AckResult<null>) => void) => void;
  'game:removeBot': (payload: { gameId: string; botId: string }, ack?: (res: AckResult<null>) => void) => void;
  'game:leave': (payload: { gameId: string }, ack?: (res: AckResult<null>) => void) => void;
  'game:start': (payload: { gameId: string }, ack?: (res: AckResult<null>) => void) => void;
  'game:restart': (payload: { gameId: string }, ack?: (res: AckResult<null>) => void) => void;
  'game:ready': (payload: { gameId: string }, ack?: (res: AckResult<null>) => void) => void;
  'game:options': (payload: { gameId: string; options: Partial<GameOptions> }, ack?: (res: AckResult<null>) => void) => void;
  'game:move': (payload: { gameId: string; move: Move }, ack?: (res: AckResult<null>) => void) => void;
  'game:chat': (payload: { gameId: string; text: string }) => void;
  'game:reaction': (payload: { gameId: string; kind: ReactionKind }) => void;
  'game:sync': (payload: { gameId: string }) => void;
  'push:invite': (payload: { gameId: string; toUserId: string }, ack?: (res: AckResult<null>) => void) => void;
}

/** Events server -> client */
export interface ServerToClientEvents {
  'game:state': (view: PlayerView) => void;
  'game:summary': (summary: GameSummary) => void;
  'game:chat': (entry: { from: string; name: string; text: string; ts: number }) => void;
  'game:reaction': (entry: { from: string; name: string; kind: ReactionKind; ts: number }) => void;
  'game:ended': (payload: { gameId: string; durakId: string | null; finishOrder: string[] }) => void;
  'lobby:list': (games: GameSummary[]) => void;
  'error:msg': (msg: string) => void;
  /** Live in-app game invite — emitted in addition to the web-push so users
   *  with the app already open get a richer toast they can act on. */
  'invite:received': (payload: { gameId: string; code: string; from: { id: string; name: string; avatar: string | null } }) => void;
  /** Friend events */
  'friend:online': (payload: { userId: string }) => void;
  'friend:offline': (payload: { userId: string }) => void;
  'friend:request': (payload: { id: string; from: { id: string; name: string; avatar: string | null } }) => void;
  'friend:requestResponded': (payload: { id: string; accepted: boolean }) => void;
  'friend:removed': (payload: Record<string, never>) => void;
}

export type AckResult<T> = { ok: true; data: T } | { ok: false; error: string };
